/**
 * VectorStylePanel — Card 4 §"Style: stroke / fill / icon / labels".
 *
 * Pure (state lifted to the caller). Renders native color pickers for
 * stroke and fill, a free stroke-width number input, fill opacity,
 * a point-icon picker, and a labels toggle + label-from-field
 * selector. All controls disable under `readOnly` and surface a
 * `lock` icon with an explanatory tooltip per WP-4 acceptance.
 *
 * Source-of-truth citations:
 *   - geosoft-platform-design-system/docs/DESIGN_POLICY.md §5
 *   - geosoft-platform-design-system/docs/ICON_SYSTEM_PLAYBOOK.md
 *   - docs/results/VIEWER-TOOLBAR-AND-GTWIN-FIRST-UX-MASTERPLAN.md §7.4
 */
import { useId } from 'react';
import { Icon, Stack, Text, Tooltip, tokens, type IconName } from '@/design';
import { type VectorStyleState } from './vectorStyleModel';
import { pointSymbolRenderLabel, pointSymbolSupportsRotation } from './pointSymbolShape';
import { PointSymbolPicker } from './PointSymbolPicker';
import { PolygonSymbolPicker } from './PolygonSymbolPicker';
import { LineSymbolPicker } from './LineSymbolPicker';
import { UniqueValueSymbologyPanel } from './UniqueValueSymbologyPanel';
import type { IdentifyGeometryKind } from './vectorIdentifyModel';

export interface VectorStylePanelProps {
  readonly state: VectorStyleState;
  readonly geometryKind?: IdentifyGeometryKind;
  readonly availableLabelFields: ReadonlyArray<string>;
  readonly features?: ReadonlyArray<{
    readonly properties?: Readonly<Record<string, unknown>>;
  }>;
  readonly readOnly: boolean;
  readonly onChange: (next: VectorStyleState) => void;
  readonly 'data-testid'?: string;
}

const FILL_OPACITY_OPTIONS = [0, 0.1, 0.2, 0.3, 0.5, 0.7, 1] as const;
const STROKE_WIDTH_MIN = 0;
const STROKE_WIDTH_MAX = 64;
const POINT_ROTATION_OPTIONS = [0, 45, 90, 135, 180, 225, 270, 315] as const;

export function VectorStylePanel({
  state,
  geometryKind = 'unknown',
  availableLabelFields,
  features = [],
  readOnly,
  onChange,
  'data-testid': testId,
}: VectorStylePanelProps) {
  const headingId = useId();
  const strokePickerId = useId();
  const fillPickerId = useId();
  const widthPickerId = useId();
  const opacityPickerId = useId();
  const iconPickerId = useId();
  const rotationPickerId = useId();
  const patternColorId = useId();
  const patternScaleId = useId();
  const patternRotationId = useId();
  const labelFieldId = useId();

  const update = (patch: Partial<VectorStyleState>) => {
    if (readOnly) return;
    onChange({ ...state, ...patch });
  };
  const updatePolygon = (patch: Partial<VectorStyleState['polygonSymbol']>) => {
    if (readOnly) return;
    onChange({
      ...state,
      polygonSymbol: { ...state.polygonSymbol, ...patch },
    });
  };
  const updateLine = (patch: Partial<VectorStyleState['lineSymbol']>) => {
    if (readOnly) return;
    const lineSymbol = { ...state.lineSymbol, ...patch };
    onChange({
      ...state,
      lineSymbol,
      strokeColor: lineSymbol.color,
      strokeWidth: lineSymbol.width,
    });
  };

  const rotationIsMoot = !pointSymbolSupportsRotation(state.iconSwatchId);

  return (
    <section
      aria-labelledby={headingId}
      data-testid={testId}
      style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[3] }}
    >
      <Stack direction="row" align="center" gap={2}>
        <Icon name="tune" size={20} decorative />
        <Text variant="subtitle" as="h3" id={headingId} style={{ margin: 0 }}>
          Symbology
        </Text>
        {readOnly ? (
          <Tooltip content="Read-only by project permission">
            <span
              aria-label="Read-only style controls"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: tokens.space[1],
                color: tokens.color.ink.secondary,
              }}
            >
              <Icon name="lock" size={20} decorative />
            </span>
          </Tooltip>
        ) : null}
      </Stack>

      {geometryKind === 'polygon' ? (
        <>
          <label style={textLegendStyle()}>
            Primary symbology
            <select
              value={state.primarySymbology}
              disabled={readOnly}
              aria-label="Primary symbology"
              onChange={(event) =>
                update({
                  primarySymbology: event.target.value as VectorStyleState['primarySymbology'],
                })
              }
              data-testid={`${testId}-primary-symbology`}
              style={{ ...selectStyle(), width: '100%', marginTop: tokens.space[1] }}
            >
              <option value="single">Single symbol</option>
              <option value="unique-values">Unique values</option>
            </select>
          </label>

          {state.primarySymbology === 'single' ? (
          <>
            <div data-testid={`${testId}-polygon-symbol`}>
            <PolygonSymbolPicker
              value={state.polygonSymbol}
              fillOpacity={state.fillOpacity}
              strokeColor={state.strokeColor}
              strokeWidth={state.strokeWidth}
              readOnly={readOnly}
              onChange={(polygonSymbol) =>
                update({
                  polygonSymbol,
                  fillColor: polygonSymbol.backgroundColor,
                })
              }
              data-testid={`${testId}-polygon-picker`}
            />
          </div>

            {state.polygonSymbol.fillMode !== 'solid'
          && state.polygonSymbol.fillMode !== 'none' ? (
            <>
              <fieldset disabled={readOnly} style={fieldsetStyle()}>
                <label htmlFor={patternColorId} style={textLegendStyle()}>
                  Pattern color
                </label>
                <input
                  id={patternColorId}
                  type="color"
                  value={toColorInputValue(state.polygonSymbol.foregroundColor)}
                  disabled={readOnly}
                  onChange={(event) => updatePolygon({ foregroundColor: event.target.value })}
                  data-testid={`${testId}-pattern-color`}
                  style={colorInputStyle()}
                />
              </fieldset>
              <fieldset disabled={readOnly} style={fieldsetStyle()}>
                <label htmlFor={patternScaleId} style={textLegendStyle()}>
                  Pattern scale
                </label>
                <input
                  id={patternScaleId}
                  type="range"
                  min="0.5"
                  max="3"
                  step="0.25"
                  value={state.polygonSymbol.scale}
                  disabled={readOnly}
                  onChange={(event) => updatePolygon({ scale: Number(event.target.value) })}
                  data-testid={`${testId}-pattern-scale`}
                />
              </fieldset>
              <fieldset disabled={readOnly} style={fieldsetStyle()}>
                <label htmlFor={patternRotationId} style={textLegendStyle()}>
                  Pattern rotation
                </label>
                <input
                  id={patternRotationId}
                  type="range"
                  min="0"
                  max="315"
                  step="45"
                  value={state.polygonSymbol.rotation}
                  disabled={readOnly}
                  onChange={(event) => updatePolygon({ rotation: Number(event.target.value) })}
                  data-testid={`${testId}-pattern-rotation`}
                />
              </fieldset>
            </>
            ) : null}
          </>
          ) : (
            <UniqueValueSymbologyPanel
              state={state}
              geometryKind="polygon"
              availableFields={availableLabelFields}
              features={features}
              readOnly={readOnly}
              onChange={onChange}
              data-testid={`${testId}-unique-values`}
            />
          )}
        </>
      ) : null}

      {geometryKind === 'line' ? (
        <>
          <label style={textLegendStyle()}>
            Primary symbology
            <select
              value={state.primarySymbology}
              disabled={readOnly}
              aria-label="Primary symbology"
              onChange={(event) =>
                update({
                  primarySymbology: event.target.value as VectorStyleState['primarySymbology'],
                })
              }
              data-testid={`${testId}-primary-symbology`}
              style={{ ...selectStyle(), width: '100%', marginTop: tokens.space[1] }}
            >
              <option value="single">Single symbol</option>
              <option value="unique-values">Unique values</option>
            </select>
          </label>
          {state.primarySymbology === 'single' ? (
            <>
              <LineSymbolPicker
                value={state.lineSymbol}
                readOnly={readOnly}
                onChange={(lineSymbol) =>
                  update({
                    lineSymbol,
                    strokeColor: lineSymbol.color,
                    strokeWidth: lineSymbol.width,
                  })
                }
                data-testid={`${testId}-line-picker`}
              />
              <fieldset disabled={readOnly} style={fieldsetStyle()}>
                <label style={textLegendStyle()}>
                  Line color
                  <input
                    type="color"
                    value={toColorInputValue(state.lineSymbol.color)}
                    disabled={readOnly}
                    onChange={(event) => updateLine({ color: event.target.value })}
                    data-testid={`${testId}-line-color`}
                    style={colorInputStyle()}
                  />
                </label>
              </fieldset>
              <fieldset disabled={readOnly} style={fieldsetStyle()}>
                <label style={textLegendStyle()}>
                  Line width (px)
                  <input
                    type="number"
                    min={STROKE_WIDTH_MIN}
                    max={STROKE_WIDTH_MAX}
                    value={state.lineSymbol.width}
                    disabled={readOnly}
                    onChange={(event) => updateLine({ width: Number(event.target.value) })}
                    data-testid={`${testId}-line-width`}
                    style={selectStyle()}
                  />
                </label>
              </fieldset>
              <fieldset disabled={readOnly} style={fieldsetStyle()}>
                <label style={textLegendStyle()}>
                  Line opacity
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={state.lineSymbol.opacity}
                    disabled={readOnly}
                    onChange={(event) => updateLine({ opacity: Number(event.target.value) })}
                    data-testid={`${testId}-line-opacity`}
                  />
                </label>
              </fieldset>
              <fieldset disabled={readOnly} style={fieldsetStyle()}>
                <label style={textLegendStyle()}>
                  Line cap
                  <select
                    value={state.lineSymbol.cap}
                    disabled={readOnly}
                    onChange={(event) =>
                      updateLine({ cap: event.target.value as VectorStyleState['lineSymbol']['cap'] })
                    }
                    style={selectStyle()}
                  >
                    <option value="butt">Butt</option>
                    <option value="round">Round</option>
                    <option value="square">Square</option>
                  </select>
                </label>
                <label style={textLegendStyle()}>
                  Line join
                  <select
                    value={state.lineSymbol.join}
                    disabled={readOnly}
                    onChange={(event) =>
                      updateLine({ join: event.target.value as VectorStyleState['lineSymbol']['join'] })
                    }
                    style={selectStyle()}
                  >
                    <option value="bevel">Bevel</option>
                    <option value="round">Round</option>
                    <option value="miter">Miter</option>
                  </select>
                </label>
              </fieldset>
              {state.lineSymbol.pattern === 'cased' ? (
                <fieldset disabled={readOnly} style={fieldsetStyle()}>
                  <label style={textLegendStyle()}>
                    Casing color
                    <input
                      type="color"
                      value={toColorInputValue(state.lineSymbol.casingColor)}
                      disabled={readOnly}
                      onChange={(event) => updateLine({ casingColor: event.target.value })}
                      style={colorInputStyle()}
                    />
                  </label>
                  <label style={textLegendStyle()}>
                    Casing width (px)
                    <input
                      type="number"
                      min="1"
                      max="16"
                      value={state.lineSymbol.casingWidth}
                      disabled={readOnly}
                      onChange={(event) => updateLine({ casingWidth: Number(event.target.value) })}
                      style={selectStyle()}
                    />
                  </label>
                </fieldset>
              ) : null}
            </>
          ) : (
            <UniqueValueSymbologyPanel
              state={state}
              geometryKind="line"
              availableFields={availableLabelFields}
              features={features}
              readOnly={readOnly}
              onChange={onChange}
              data-testid={`${testId}-unique-values`}
            />
          )}
        </>
      ) : null}

      {geometryKind === 'point' ? (
        <>
          <fieldset
            disabled={readOnly}
            style={{ margin: 0, padding: 0, border: 0, display: 'flex', flexDirection: 'column', gap: tokens.space[1] }}
          >
            <PointSymbolPicker
              value={state.iconSwatchId}
              previewColor={state.fillColor}
              readOnly={readOnly}
              onChange={(symbolId) => update({ iconSwatchId: symbolId })}
              data-testid={`${testId}-symbol`}
            />
            <Text variant="caption" as="span">
              Renders as: <strong>{pointSymbolRenderLabel(state.iconSwatchId)}</strong>
            </Text>
          </fieldset>

          <fieldset
            aria-labelledby={`${iconPickerId}-size`}
            disabled={readOnly}
            style={{ margin: 0, padding: 0, border: 0, display: 'flex', flexDirection: 'column', gap: tokens.space[1] }}
          >
            <label htmlFor={`${iconPickerId}-size`} style={textLegendStyle()}>
              Point size (px)
            </label>
            <input
              id={`${iconPickerId}-size`}
              type="number"
              min="4"
              max="64"
              value={state.pointSize}
              disabled={readOnly}
              onChange={(event) => {
                const val = parseInt(event.target.value, 10);
                if (!isNaN(val) && val >= 4 && val <= 64) {
                  update({ pointSize: val });
                }
              }}
              style={selectStyle()}
            />
          </fieldset>

          <fieldset
            aria-labelledby={rotationPickerId}
            disabled={readOnly || rotationIsMoot}
            style={{ margin: 0, padding: 0, border: 0, display: 'flex', flexDirection: 'column', gap: tokens.space[1] }}
          >
            <legend id={rotationPickerId} style={textLegendStyle()}>
              Point rotation
            </legend>
            <div role="radiogroup" aria-labelledby={rotationPickerId} style={chipRowStyle()}>
              {POINT_ROTATION_OPTIONS.map((rotation) => (
                <SegmentedChip
                  key={rotation}
                  label={`${rotation}°`}
                  icon="rotate_right"
                  selected={rotation === state.pointRotation}
                  disabled={readOnly || rotationIsMoot}
                  onSelect={() => update({ pointRotation: rotation })}
                />
              ))}
            </div>
            {rotationIsMoot ? (
              <Text variant="caption" as="span">
                Rotation has no effect on a circular symbol.
              </Text>
            ) : null}
          </fieldset>
        </>
      ) : null}

      {geometryKind !== 'line'
      && (geometryKind !== 'polygon' || state.primarySymbology === 'single') ? (
      <>
      <fieldset
        disabled={readOnly}
        style={{ margin: 0, padding: 0, border: 0, display: 'flex', flexDirection: 'column', gap: tokens.space[1] }}
      >
        <label htmlFor={strokePickerId} style={textLegendStyle()}>
          Stroke color
        </label>
        <input
          id={strokePickerId}
          type="color"
          value={toColorInputValue(state.strokeColor)}
          disabled={readOnly}
          onChange={(event) => update({ strokeColor: event.target.value })}
          data-testid={`${testId}-stroke-color`}
          style={colorInputStyle()}
        />
      </fieldset>

      <fieldset
        disabled={readOnly}
        style={{ margin: 0, padding: 0, border: 0, display: 'flex', flexDirection: 'column', gap: tokens.space[1] }}
      >
        <label htmlFor={widthPickerId} style={textLegendStyle()}>
          Stroke width (px)
        </label>
        <input
          id={widthPickerId}
          type="number"
          min={STROKE_WIDTH_MIN}
          max={STROKE_WIDTH_MAX}
          value={state.strokeWidth}
          disabled={readOnly}
          onChange={(event) => {
            const val = Number(event.target.value);
            if (!Number.isNaN(val) && val >= STROKE_WIDTH_MIN && val <= STROKE_WIDTH_MAX) {
              update({ strokeWidth: val });
            }
          }}
          data-testid={`${testId}-stroke-width`}
          style={selectStyle()}
        />
      </fieldset>

      <fieldset
        disabled={readOnly}
        style={{ margin: 0, padding: 0, border: 0, display: 'flex', flexDirection: 'column', gap: tokens.space[1] }}
      >
        <label htmlFor={fillPickerId} style={textLegendStyle()}>
          Fill color
        </label>
        <input
          id={fillPickerId}
          type="color"
          value={toColorInputValue(state.fillColor)}
          disabled={readOnly}
          onChange={(event) =>
            update({
              fillColor: event.target.value,
              polygonSymbol: {
                ...state.polygonSymbol,
                backgroundColor: event.target.value,
              },
            })
          }
          data-testid={`${testId}-fill-color`}
          style={colorInputStyle()}
        />
      </fieldset>

      {/* Fill opacity */}
      <fieldset
        aria-labelledby={opacityPickerId}
        disabled={readOnly}
        style={{ margin: 0, padding: 0, border: 0, display: 'flex', flexDirection: 'column', gap: tokens.space[1] }}
      >
        <legend id={opacityPickerId} style={textLegendStyle()}>
          Fill opacity
        </legend>
        <div role="radiogroup" aria-labelledby={opacityPickerId} style={chipRowStyle()}>
          {FILL_OPACITY_OPTIONS.map((opacity) => (
            <SegmentedChip
              key={opacity}
              label={`${Math.round(opacity * 100)}%`}
              icon="texture"
              selected={opacity === state.fillOpacity}
              disabled={readOnly}
              onSelect={() => update({ fillOpacity: opacity })}
            />
          ))}
        </div>
      </fieldset>
      </>
      ) : null}

      {/* Labels */}
      <Stack direction="column" gap={1}>
        <label
          htmlFor={`${labelFieldId}-toggle`}
          style={{ ...textLegendStyle(), display: 'flex', alignItems: 'center', gap: tokens.space[2] }}
        >
          <Icon name="label" size={20} decorative />
          Labels
        </label>
        <Stack direction="row" align="center" gap={2}>
          <input
            id={`${labelFieldId}-toggle`}
            type="checkbox"
            checked={state.labelsEnabled}
            disabled={readOnly}
            onChange={(event) => update({ labelsEnabled: event.target.checked })}
            style={{ accentColor: tokens.color.accent.primary }}
          />
          <Text variant="caption" as="span">
            {state.labelsEnabled ? 'Labels on' : 'Labels off'}
          </Text>
        </Stack>
        <label htmlFor={labelFieldId} style={textLegendStyle()}>
          Label from field
        </label>
        <select
          id={labelFieldId}
          value={state.labelField ?? ''}
          disabled={readOnly || !state.labelsEnabled}
          onChange={(event) =>
            update({ labelField: event.target.value === '' ? null : event.target.value })
          }
          style={selectStyle()}
        >
          <option value="">— Choose attribute —</option>
          {availableLabelFields.map((field) => (
            <option key={field} value={field}>
              {field}
            </option>
          ))}
        </select>
      </Stack>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Internal sub-components
// ---------------------------------------------------------------------------

interface SegmentedChipProps {
  readonly label: string;
  readonly icon: IconName;
  readonly selected: boolean;
  readonly disabled: boolean;
  readonly onSelect: () => void;
}

function SegmentedChip({ label, icon, selected, disabled, onSelect }: SegmentedChipProps) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      disabled={disabled}
      onClick={onSelect}
      style={{
        appearance: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        gap: tokens.space[1],
        padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
        minHeight: tokens.density.touchMin,
        background: selected ? tokens.color.surface[3] : tokens.color.surface[2],
        border: selected
          ? `${tokens.border.width.thin}px solid ${tokens.color.accent.primary}`
          : `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
        borderRadius: tokens.radius.pill,
        color: tokens.color.ink.primary,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        fontFamily: tokens.font.family.ui,
        fontSize: tokens.font.size.sm,
      }}
    >
      <Icon name={icon} size={20} decorative />
      <span>{label}</span>
    </button>
  );
}

function textLegendStyle() {
  return {
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.role.label.size,
    fontWeight: tokens.font.role.label.weight,
    color: tokens.color.ink.secondary,
  } as const;
}

function toColorInputValue(color: string): string {
  const hex = color.trim();
  if (/^#[0-9a-fA-F]{6}$/.test(hex)) return hex;
  return tokens.color.accent.primary;
}

function colorInputStyle() {
  return {
    width: tokens.density.touchMin,
    height: tokens.density.touchMin,
    padding: 0,
    border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
    borderRadius: tokens.radius.md,
    background: tokens.color.surface[2],
    cursor: 'pointer',
  } as const;
}

function fieldsetStyle() {
  return {
    margin: 0,
    padding: 0,
    border: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.space[1],
  } as const;
}

function chipRowStyle() {
  return {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.space[1],
  } as const;
}

function selectStyle() {
  return {
    minHeight: tokens.density.touchMin,
    background: tokens.color.surface[2],
    color: tokens.color.ink.primary,
    border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
    borderRadius: tokens.radius.input,
    paddingInline: tokens.space[2],
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.size.sm,
  } as const;
}
